import { isSemanticModelGeneralization } from "@dataspecer/core-v2/semantic-model/concepts";
import { extractModelObjects } from "../../layout-algorithms/entity-bundles.js";
import { isVisualNode, isVisualRelationship, isVisualProfileRelationship, VISUAL_NODE_TYPE, isVisualGroup, isVisualDiagramNode } from "@dataspecer/visual-model";
import { findTopLevelGroup, getBotRightPosition, getGroupMappings, getNonGroupNodesInGroup, getTopLeftPosition, PhantomElementsFactory, placePositionOnGrid, } from "../../util/utils.js";
import { ReactflowDimensionsEstimator } from "../../index.js";
import { getDomainAndRange } from "@dataspecer/core-v2/semantic-model/relationship-utils";
import { addNodeToGraph, getAllEdges, getAllIncomingEdges, getAllOutgoingEdges, isNodeInVisualModel, VisualNodeComplete, getAllIncomingUniqueEdges, getAllOutgoingUniqueEdges, getAllUniqueEdges } from "./node.js";
import { GraphFactory } from "./graph-factory.js";
import { convertOutgoingEdgeTypeToIncoming, DefaultEdge, getEdgeTypeNameFromEdge } from "./edge.js";
/**
 * Class which stores (sub)graph.
 */
export class DefaultGraph {
    initializeGraphMetadata(mainGraph, sourceGraph, graphIdentifier, isDummy, nodeDimensionQueryHandler) {
        this.sourceGraph = sourceGraph;
        if (!(this instanceof DefaultMainGraph)) {
            mainGraph.allNodes[graphIdentifier] = this;
        }
        else {
            if (nodeDimensionQueryHandler === undefined || nodeDimensionQueryHandler === null) {
                nodeDimensionQueryHandler = new ReactflowDimensionsEstimator();
            }
            this.nodeDimensionQueryHandler = nodeDimensionQueryHandler;
        }
        this.mainGraph = mainGraph;
        this.id = graphIdentifier;
        this.isDummy = isDummy;
        this.sourceModelIdentifier = null;
    }
    initializeWithGivenContent(mainGraph, sourceGraph, graphIdentifier, nodeContentOfGraph, isDummy, nodeDimensionQueryHandler) {
        this.initializeGraphMetadata(mainGraph, sourceGraph, graphIdentifier, isDummy, nodeDimensionQueryHandler);
        const nodesMap = {};
        for (const node of nodeContentOfGraph) {
            nodesMap[node.id] = node;
            node.setSourceGraph(this);
        }
        this.nodes = nodesMap;
        const visualNodeRepresentingGraph = this.createNewVisualNodeForGraphBasedOnContainedNodes();
        const topLeft = getTopLeftPosition(Object.values(this.nodes));
        const botRight = getBotRightPosition(Object.values(this.nodes));
        const width = botRight.x - topLeft.x;
        const height = botRight.y - topLeft.y;
        visualNodeRepresentingGraph.position = { ...topLeft, anchored: null };
        this.completeVisualNode = new VisualNodeComplete(visualNodeRepresentingGraph, width, height, false, false);
    }
    createNewVisualNodeForGraphBasedOnContainedNodes() {
        const position = getTopLeftPosition(Object.values(this.nodes));
        return {
            id: this.id,
            type: [VISUAL_NODE_TYPE],
            representedEntity: this.id,
            position: {
                ...position,
                anchored: null,
            },
            content: [],
            visualModels: [],
            model: "",
        };
    }
    /**
     * Initializes instance of this graph, the reason why this is not part of the constructor is that since {@link DefaultMainGraph} extends this class,
     * it uses the same initialize method, that results in situation that we can't update the list of all nodes in the constructor, since they are not initialized.
     */
    initialize(mainGraph, sourceGraph, graphIdentifier, inputModels, isDummy, visualModel, entitiesToLayout, nodeDimensionQueryHandler, explicitAnchors) {
        this.initializeGraphMetadata(mainGraph, sourceGraph, graphIdentifier, isDummy, nodeDimensionQueryHandler);
        if (inputModels === null) {
            return;
        }
        // https://stackoverflow.com/questions/46703364/why-does-instanceof-in-typescript-give-me-the-error-foo-only-refers-to-a-ty
        // Just simple check, dont check all elements it is not necessary
        const areInputModelsExtractedModels = inputModels.entities && inputModels.generalizations;
        const extractedModels = areInputModelsExtractedModels ? inputModels : extractModelObjects(inputModels);
        this.createGraphNodes(entitiesToLayout, visualModel, extractedModels, explicitAnchors);
        this.createGraphEdges(entitiesToLayout, visualModel, extractedModels);
        this.createGraphGroups(entitiesToLayout, visualModel, extractedModels);
    }
    createGraphNodes(entitiesToLayout, visualModel, extractedModels, explicitAnchors) {
        if (visualModel !== null) {
            const nodes = entitiesToLayout.visualEntities
                .map(entity => visualModel.getVisualEntity(entity))
                .filter(entity => entity !== null)
                .filter(entity => isVisualNode(entity) || isVisualDiagramNode(entity));
            for (const node of nodes) {
                if (isVisualNode(node)) {
                    const cclass = extractedModels.classes.find(cclass => cclass.semanticClass.id === node.representedEntity);
                    if (cclass !== undefined) {
                        addNodeToGraph(this.mainGraph, node, cclass.semanticClass, false, cclass.sourceModelIdentifier, extractedModels, this, visualModel, entitiesToLayout, null, false, explicitAnchors);
                    }
                    else {
                        const classProfile = extractedModels.classesProfiles
                            .find(classProfile => classProfile.semanticClassProfile.id === node.representedEntity);
                        if (classProfile === undefined) {
                            addNodeToGraph(this.mainGraph, node, null, true, classProfile?.sourceModelIdentifier ?? null, extractedModels, this, visualModel, entitiesToLayout, null, false, explicitAnchors);
                        }
                        else {
                            addNodeToGraph(this.mainGraph, node, classProfile.semanticClassProfile, true, classProfile.sourceModelIdentifier, extractedModels, this, visualModel, entitiesToLayout, null, false, explicitAnchors);
                        }
                    }
                }
                else {
                    addNodeToGraph(this.mainGraph, node, null, true, null, extractedModels, this, visualModel, entitiesToLayout, null, false, explicitAnchors);
                }
            }
        }
        for (const [outsider, position] of Object.entries(entitiesToLayout.outsiders)) {
            // Almost the same as for the visual nodes, but there is difference for nodes without semantic equivalents
            const cclass = extractedModels.classes.find(cclass => cclass.semanticClass.id === outsider);
            if (cclass !== undefined) {
                addNodeToGraph(this.mainGraph, null, cclass.semanticClass, false, cclass.sourceModelIdentifier, extractedModels, this, visualModel, entitiesToLayout, position, false, explicitAnchors);
            }
            else {
                const classProfile = extractedModels.classesProfiles
                    .find(classProfile => classProfile.semanticClassProfile.id === outsider);
                if (classProfile === undefined) {
                    console.error("outsider is neither class or class profile");
                    throw new Error("Implementation error");
                    return;
                }
                addNodeToGraph(this.mainGraph, null, classProfile.semanticClassProfile, true, classProfile.sourceModelIdentifier, extractedModels, this, visualModel, entitiesToLayout, position, false, explicitAnchors);
            }
        }
    }
    /**
     * Expects that the ends of edges to already exist.
     */
    createGraphEdges(entitiesToLayout, visualModel, extractedModels) {
        const sourceGraph = this;
        if (visualModel !== null) {
            const relationshipEdges = entitiesToLayout.visualEntities
                .map(visualId => visualModel.getVisualEntity(visualId))
                .filter(entity => entity !== null)
                .filter(edge => isVisualRelationship(edge));
            const classProfilesEdges = entitiesToLayout.visualEntities
                .map(visualId => visualModel.getVisualEntity(visualId))
                .filter(entity => entity !== null)
                .filter(edge => isVisualProfileRelationship(edge));
            for (const edge of relationshipEdges) {
                let semanticRelationshipBundle = getSemanticRelationshipBundle(edge.representedRelationship, extractedModels);
                if (semanticRelationshipBundle === undefined || semanticRelationshipBundle === null) {
                    // If this causes errors, just put here continue to skip it, but so far it seems to be fine.
                    console.warn("The visual relationship is not present in the semantic model, but we will try to put it into graph anyways");
                }
                let semanticRelationship = convertAllowedEdgeBundleToAllowedEdgeType(semanticRelationshipBundle);
                DefaultEdge.addNewEdgeToGraph(sourceGraph, null, edge, semanticRelationship, edge.visualSource, edge.visualTarget, extractedModels, semanticRelationshipBundle?.type);
            }
            for (const edge of classProfilesEdges) {
                const semanticClassProfile = getSemanticRelationshipBundle(edge.entity, extractedModels);
                if (semanticClassProfile === undefined) {
                    // Here we skip it using continue unlike in relationship edges, don't allow profiles without semantic coutner part
                    console.warn("The visual relationship is not present in the semantic model");
                    continue;
                }
                DefaultEdge.addNewEdgeToGraph(sourceGraph, null, edge, null, edge.visualSource, edge.visualTarget, extractedModels, "outgoingClassProfileEdges");
            }
        }
        const relationships = extractedModels.relationships
            .filter(relationshipBundle => {
            return checkIfEdgeShouldBePartOfGraph(visualModel, entitiesToLayout, relationshipBundle.semanticRelationship.ends[0].concept, relationshipBundle.semanticRelationship.ends[1].concept);
        })
            .map(relationshipBundle => relationshipBundle.semanticRelationship);
        relationships.forEach((relationship) => {
            const { domain, range } = getDomainAndRange(relationship);
            const semanticStart = domain.concept;
            const semanticEnd = range.concept;
            if (entitiesToLayout.outsiders[semanticStart] === undefined && // It was already added
                entitiesToLayout.outsiders[semanticEnd] === undefined) {
                return;
            }
            let visualStarts = this.mainGraph.semanticNodeToVisualMap[semanticStart];
            let visualEnds = this.mainGraph.semanticNodeToVisualMap[semanticEnd];
            for (const visualStart of visualStarts) {
                for (const visualEnd of visualEnds) {
                    DefaultEdge.addNewEdgeToGraph(sourceGraph, null, null, relationship, visualStart.id, visualEnd.id, extractedModels, "outgoingRelationshipEdges");
                }
            }
        });
        //
        const generalizations = extractedModels.generalizations
            .filter(generalizationBundle => {
            return checkIfEdgeShouldBePartOfGraph(visualModel, entitiesToLayout, generalizationBundle.semanticGeneralization.child, generalizationBundle.semanticGeneralization.parent);
        })
            .map(generalizationBundle => generalizationBundle.semanticGeneralization);
        generalizations.forEach((generalization) => {
            const semanticStart = generalization.child;
            const semanticEnd = generalization.parent;
            if (entitiesToLayout.outsiders[semanticStart] === undefined && // It was already added
                entitiesToLayout.outsiders[semanticEnd] === undefined) {
                return;
            }
            let visualStarts = this.mainGraph.semanticNodeToVisualMap[semanticStart];
            let visualEnds = this.mainGraph.semanticNodeToVisualMap[semanticEnd];
            for (const visualStart of visualStarts) {
                for (const visualEnd of visualEnds) {
                    DefaultEdge.addNewEdgeToGraph(sourceGraph, null, null, generalization, visualStart.id, visualEnd.id, extractedModels, "outgoingGeneralizationEdges");
                }
            }
        });
        //
        const relationshipsProfiles = extractedModels.relationshipsProfiles
            .filter(relationshipProfileBundle => {
            return checkIfEdgeShouldBePartOfGraph(visualModel, entitiesToLayout, relationshipProfileBundle.semanticRelationshipProfile.ends[0].concept, relationshipProfileBundle.semanticRelationshipProfile.ends[1].concept);
        })
            .map(relationshipProfileBundle => relationshipProfileBundle.semanticRelationshipProfile);
        relationshipsProfiles.forEach((relationshipProfile) => {
            const semanticStart = relationshipProfile.ends[0].concept;
            const semanticEnd = relationshipProfile.ends[1].concept;
            if (entitiesToLayout.outsiders[semanticStart] === undefined && // It was already added
                entitiesToLayout.outsiders[semanticEnd] === undefined) {
                return;
            }
            let visualStarts = this.mainGraph.semanticNodeToVisualMap[semanticStart];
            let visualEnds = this.mainGraph.semanticNodeToVisualMap[semanticEnd];
            for (const visualStart of visualStarts) {
                for (const visualEnd of visualEnds) {
                    DefaultEdge.addNewEdgeToGraph(sourceGraph, null, null, relationshipProfile, visualStart.id, visualEnd.id, extractedModels, "outgoingProfileEdges");
                }
            }
        });
        //
        const toClassProfileEdges = [];
        for (const classProfileBundle of extractedModels.classesProfiles) {
            for (const profileOf of classProfileBundle.semanticClassProfile.profiling) {
                if (checkIfEdgeShouldBePartOfGraph(visualModel, entitiesToLayout, classProfileBundle.semanticClassProfile.id, profileOf)) {
                    toClassProfileEdges.push({
                        source: classProfileBundle.semanticClassProfile,
                        target: profileOf
                    });
                }
            }
        }
        for (const toClassProfileEdge of toClassProfileEdges) {
            const semanticStart = toClassProfileEdge.source.id;
            const semanticEnd = toClassProfileEdge.target;
            if (entitiesToLayout.outsiders[semanticStart] === undefined && // It was already added
                entitiesToLayout.outsiders[semanticEnd] === undefined) {
                return;
            }
            let visualStarts = this.mainGraph.semanticNodeToVisualMap[semanticStart];
            let visualEnds = this.mainGraph.semanticNodeToVisualMap[semanticEnd];
            for (const visualStart of visualStarts) {
                for (const visualEnd of visualEnds) {
                    DefaultEdge.addNewEdgeToGraph(sourceGraph, null, null, null, visualStart.id, visualEnd.id, extractedModels, "outgoingClassProfileEdges");
                }
            }
        }
    }
    createGraphGroups(entitiesToLayout, visualModel, extractedModels) {
        if (visualModel !== null) {
            const groups = entitiesToLayout.visualEntities
                .map(entity => visualModel.getVisualEntity(entity))
                .filter(entity => entity !== null)
                .filter(isVisualGroup);
            const { nodeToGroupMapping, existingGroups } = getGroupMappings(groups);
            const alreadyProcessedGroups = {};
            for (const group of groups) {
                const topLevelGroup = findTopLevelGroup(group.id, existingGroups, nodeToGroupMapping);
                if (alreadyProcessedGroups[topLevelGroup] === true) {
                    continue;
                }
                alreadyProcessedGroups[topLevelGroup] = true;
                const { nonGroupNodes, processedGroups } = getNonGroupNodesInGroup(existingGroups[topLevelGroup], existingGroups);
                for (const processedGroup of Object.keys(processedGroups)) {
                    alreadyProcessedGroups[processedGroup] = true;
                }
                const nodesInSubgraph = nonGroupNodes
                    .map(identifier => this.mainGraph.findNodeInAllNodes(identifier));
                GraphFactory.createGraph(this.mainGraph, this, topLevelGroup, nodesInSubgraph, true, true);
            }
        }
    }
    isEdgeInsideGraph(edge) {
        return edge.start.getSourceGraph() === this || edge.end.getSourceGraph() === this;
    }
    createGeneralizationSubgraphs() {
        const generalizationEdges = [];
        this.mainGraph.getAllEdgesInMainGraph().forEach(edge => {
            if (isSemanticModelGeneralization(edge.semanticEntityRepresentingEdge) && this.isEdgeInsideGraph(edge)) {
                generalizationEdges.push(edge.semanticEntityRepresentingEdge);
            }
        });
        // We pass in no visual model, since it is expected that the edges in the graph are exactly those which were at the visual model at the time of creation
        this.createGeneralizationSubgraphsInternal(generalizationEdges, null);
    }
    /**
     * Creates generalization subgraphs. The subgraphs are maximal, meaning any node which can be reached through the generalization path is in the subgraph.
     */
    createGeneralizationSubgraphsInternal(generalizationEdges, visualModel) {
        // For now 1 whole hierarchy (n levels) == 1 subgraph
        let parents = {};
        let children = {};
        generalizationEdges.forEach(g => {
            if (parents[g.child] === undefined) {
                parents[g.child] = [];
            }
            parents[g.child].push(g.parent);
            if (children[g.parent] === undefined) {
                children[g.parent] = [];
            }
            children[g.parent].push(g.child);
        });
        const subgraphs = this.findGeneralizationSubgraphs(parents, children);
        let generalizationSubgraphs = subgraphs.map(subgraph => {
            // Just paste in the original node, don't copy
            const generalizationSubgraphNonFlattened = subgraph
                .map(nodeID => {
                const result = [];
                for (const node of this.mainGraph.semanticNodeToVisualMap[nodeID]) {
                    result.push(this.nodes[node.id]);
                }
                return result;
            });
            return generalizationSubgraphNonFlattened.flat();
        });
        generalizationSubgraphs = generalizationSubgraphs.map(subgraph => subgraph.filter(node => node !== undefined));
        let createdSubgraphs = [];
        generalizationSubgraphs.forEach(nodesInSubgraph => {
            createdSubgraphs.push(this.createNewGraphAndInsertInGraph(nodesInSubgraph));
        });
        return createdSubgraphs;
    }
    /**
     * Finds generalization subgraphs. The subgraphs are maximal, meaning any node which can be reached through the generalization path is in the subgraph.
     * @param parents is record maps node id to its parents (in the generalization hierarchy)
     * @param children is record maps node id to its children (in the generalization hierarchy)
     * @returns Returns 2D string array representing the subgraphs. So each string[] is one generalization subgraph.
     */
    findGeneralizationSubgraphs(parents, children) {
        let subgraphs = {};
        let stack = [];
        let currSubgraph = -1;
        for (let [child, concreteParents] of Object.entries(parents)) {
            if (subgraphs[child] === undefined) {
                currSubgraph++;
                stack.push(child);
                subgraphs[stack[0]] = currSubgraph;
            }
            while (stack.length > 0) {
                let currNode = stack.pop();
                parents[currNode] = parents[currNode] === undefined ? [] : parents[currNode];
                children[currNode] = children[currNode] === undefined ? [] : children[currNode];
                parents[currNode].concat(children[currNode]).forEach(n => {
                    if (subgraphs[n] === undefined) {
                        subgraphs[n] = currSubgraph;
                        stack.push(n);
                    }
                });
            }
        }
        currSubgraph++; // So it is the same as number of subgraphs
        let subgraphsAsArrays = [];
        for (let i = 0; i < currSubgraph; i++) {
            subgraphsAsArrays.push([]);
        }
        Object.entries(subgraphs).forEach(([nodeID, subgraphID]) => subgraphsAsArrays[subgraphID].push(nodeID));
        return subgraphsAsArrays;
    }
    // We have it as instance method, this way the identifiers are consistent between two graphs
    subgraphUniqueIdentifier = 0;
    createUniqueGeneralizationSubgraphIdentifier() {
        const identifier = `subgraph-${this.subgraphUniqueIdentifier}`;
        this.subgraphUniqueIdentifier++;
        return identifier;
    }
    createNewGraphAndInsertInGraph(nodesInSubgraph) {
        const identifier = this.createUniqueGeneralizationSubgraphIdentifier();
        const subgraph = GraphFactory.createGraph(this.mainGraph, this, identifier, nodesInSubgraph, true, true);
        return subgraph;
    }
    insertSubgraphToGraph(subgraph, nodesInSubgraph, shouldSplitEdges) {
        // Repair the old graph by substituting the nodes by the newly created subgraph
        this.replaceNodesInOriginalGraphWithTheSubgraph(subgraph, nodesInSubgraph);
        if (shouldSplitEdges) {
            // Repair edges by splitting them into two parts
            this.splitEdgesGoingBeyondSubgraph(subgraph, nodesInSubgraph);
        }
    }
    /**
     * Replaces the {@link nodesInSubgraph} stored in this instance of graph with one node (respectively graph) which stores them - the {@link subgraph}
     */
    replaceNodesInOriginalGraphWithTheSubgraph(subgraph, nodesInSubgraph) {
        for (const nodeInSubgraph of nodesInSubgraph) {
            delete this.nodes[nodeInSubgraph.id];
        }
        this.nodes[subgraph.id] = subgraph;
    }
    /**
     * Splits edges, the act of splitting is explained the {@link GraphFactory.createGraph}
     */
    splitEdgesGoingBeyondSubgraph(subgraph, changedNodes) {
        this.splitEdgesGoingBeyondSubgraphInternal(subgraph, changedNodes, "sources");
        this.splitEdgesGoingBeyondSubgraphInternal(subgraph, changedNodes, "targets");
    }
    /**
     *
     * @param edgeEnd is either "sources" or "targets", if it is sources then it repairs edges going out of subgraph, "targets" then going in
     */
    splitEdgesGoingBeyondSubgraphInternal(subgraph, changedNodes, edgeEnd) {
        const edgesGoingBeyond = [];
        const nodesInsideSubgraph = changedNodes.concat(subgraph);
        if (edgeEnd === "sources") {
            changedNodes.forEach(node => {
                for (const edge of node.getAllOutgoingEdges()) {
                    if (nodesInsideSubgraph.find(changedNode => changedNode.id === edge.end.id) === undefined) {
                        edgesGoingBeyond.push(edge);
                    }
                }
            });
        }
        else if (edgeEnd === "targets") {
            changedNodes.forEach(node => {
                for (const edge of node.getAllIncomingEdges()) {
                    if (nodesInsideSubgraph.find(changedNode => changedNode.id === edge.start.id) === undefined) {
                        edgesGoingBeyond.push(edge);
                    }
                }
            });
        }
        edgesGoingBeyond.forEach(e => this.splitEdgeIntoTwo(e, subgraph));
    }
    /**
     * Performs the edge splitting - Constructs identifiers of the new edges, removes the old one and creates two new ones.
     * One going from the start node to the subgraph and the other from the subgraph to the other end.
     */
    splitEdgeIntoTwo(edge, subgraph) {
        this.removeEdge(edge);
        const firstSplitIdentifier = PhantomElementsFactory.constructSplitID(edge.id, 0);
        const secondSplitIdentifier = PhantomElementsFactory.constructSplitID(edge.id, 1);
        DefaultEdge.addNewEdgeToGraph(subgraph, firstSplitIdentifier, null, edge.semanticEntityRepresentingEdge, edge.visualEdge.visualEdge.visualSource, subgraph.id, null, getEdgeTypeNameFromEdge(edge));
        DefaultEdge.addNewEdgeToGraph(subgraph, secondSplitIdentifier, null, edge.semanticEntityRepresentingEdge, subgraph.id, edge.visualEdge.visualEdge.visualTarget, null, getEdgeTypeNameFromEdge(edge));
    }
    /**
     * Removes given edge from nodes on both ends and also removes it from list of all edges of the main graph.
     */
    removeEdge(edge) {
        const edgeType = getEdgeTypeNameFromEdge(edge);
        const reverseEdgeType = convertOutgoingEdgeTypeToIncoming(edgeType);
        let index = edge.start[edgeType].indexOf(edge);
        edge.start[edgeType].splice(index, 1);
        index = edge.end[reverseEdgeType].indexOf(edge);
        edge.end[reverseEdgeType].splice(index, 1);
        this.mainGraph.removeFromAllEdges(edge.id);
    }
    /**
     * Removes {@link node} and all related edges and also removes it all from list of all nodes and all edges of the main graph.
     */
    removeNode(node) {
        for (const edge of node.getAllEdges()) {
            this.removeEdge(edge);
        }
        this.mainGraph.removeFromAllNodes(node.id);
    }
    convertToDataspecerRepresentation() {
        return this.completeVisualNode?.coreVisualNode ?? null;
    }
    nodes = {};
    sourceGraph;
    mainGraph;
    id = "";
    sourceModelIdentifier;
    semanticEntityRepresentingNode = null;
    isDummy = true;
    isProfile = false;
    isConsideredInLayout = true;
    layoutOptions = {};
    setOutgoingClassProfileEdge(outgoingClassProfileEdge) {
        throw new Error("Graphs can't be profiled.");
    }
    outgoingClassProfileEdges = [];
    incomingClassProfileEdges = [];
    outgoingRelationshipEdges = [];
    incomingRelationshipEdges = [];
    outgoingGeneralizationEdges = [];
    incomingGeneralizationEdges = [];
    outgoingProfileEdges = [];
    incomingProfileEdges = [];
    getAllIncomingEdges() {
        return getAllIncomingEdges(this);
    }
    getAllOutgoingEdges() {
        return getAllOutgoingEdges(this);
    }
    getAllEdges() {
        return getAllEdges(this);
    }
    getAllIncomingUniqueEdges() {
        return getAllIncomingUniqueEdges(this, null);
    }
    getAllOutgoingUniqueEdges() {
        return getAllOutgoingUniqueEdges(this, null);
    }
    getAllUniqueEdges() {
        return getAllUniqueEdges(this);
    }
    completeVisualNode;
    getAttributes() {
        return [];
    }
    getSourceGraph() {
        return this.sourceGraph;
    }
    setSourceGraph(sourceGraph) {
        this.sourceGraph = sourceGraph;
    }
    static isVisualNodeComplete(possibleNode) {
        return "coreVisualNode" in possibleNode;
    }
}
export class DefaultMainGraph extends DefaultGraph {
    semanticNodeToVisualMap = {};
    semanticEdgeToVisualMap = {};
    allNodes = {};
    allEdges = {};
    nodeDimensionQueryHandler;
    findNodeInAllNodes(nodeIdentifier) {
        return this.allNodes[nodeIdentifier] ?? null;
    }
    findEdgeInAllEdges(edgeIdentifier) {
        return this.allEdges[edgeIdentifier] ?? null;
    }
    getAllNodesInMainGraph() {
        return Object.values(this.allNodes);
    }
    getAllEdgesInMainGraph() {
        return Object.values(this.allEdges);
    }
    insertInAllNodes(node) {
        this.allNodes[node.id] = node;
    }
    insertInAllEdges(edge) {
        this.allEdges[edge.id] = edge;
    }
    removeFromAllNodes(nodeIdentifier) {
        delete this.allNodes[nodeIdentifier];
    }
    removeFromAllEdges(edgeIdentifier) {
        delete this.allEdges[edgeIdentifier];
    }
    resetForNewLayout() {
        this.getAllNodesInMainGraph().forEach(node => {
            node.isConsideredInLayout = true;
        });
        this.getAllEdgesInMainGraph().forEach(edge => {
            edge.reverseInLayout = false;
            edge.isConsideredInLayout = true;
        });
    }
    convertWholeGraphToDataspecerRepresentation() {
        const visualEntities = {};
        for (const node of this.getAllNodesInMainGraph()) {
            if (node.isDummy) {
                continue;
            }
            const visualEntityForNode = node.convertToDataspecerRepresentation();
            // TODO Hard to solve by myself - Radstr: We want to place it on grid, but
            // we can't access the cme configuration (applications/conceptual-model-editor/src/application/configuration.ts) from here
            // So I am not sure how one should solve this.
            placePositionOnGrid(visualEntityForNode.position, 10, 10);
            // node.id should be the same as visualEntityForNode.identifier
            visualEntities[visualEntityForNode.id] = {
                visualEntity: visualEntityForNode,
                isOutsider: node.completeVisualNode.isOutsider
            };
        }
        for (const edge of this.getAllEdgesInMainGraph()) {
            if (edge.isDummy) {
                continue;
            }
            const visualEntityForEdge = edge.convertToDataspecerRepresentation();
            // Just in case look-up if not set, but I think that in current version this never occurs, we always have the visual node to use
            if (visualEntityForEdge.visualSource === "") {
                const sourceGraphNode = this.findNodeInAllNodes(edge.start.id);
                visualEntityForEdge.visualSource = sourceGraphNode.completeVisualNode.coreVisualNode.id;
            }
            if (visualEntityForEdge.visualTarget === "") {
                const targetGraphNode = this.findNodeInAllNodes(edge.end.id);
                visualEntityForEdge.visualTarget = targetGraphNode.completeVisualNode.coreVisualNode.id;
            }
            if (PhantomElementsFactory.isSplitID(edge.id)) {
                visualEntityForEdge.waypoints = [];
                visualEntityForEdge.id = PhantomElementsFactory.deconstructSplitID(edge.id);
            }
            // Again edge.id should be the same as visualEntityForEdge.identifier
            visualEntities[visualEntityForEdge.id] = {
                visualEntity: visualEntityForEdge,
                isOutsider: edge.visualEdge.isOutsider,
            };
        }
        return visualEntities;
    }
}
function convertAllowedEdgeBundleToAllowedEdgeType(bundle) {
    if (bundle === null || bundle === undefined) {
        return null;
    }
    if (bundle.type === "outgoingRelationshipEdges") {
        return bundle.semanticRelationship.semanticRelationship;
    }
    else if (bundle.type === "outgoingGeneralizationEdges") {
        return bundle.semanticRelationship.semanticGeneralization;
    }
    else if (bundle.type === "outgoingProfileEdges") {
        return bundle.semanticRelationship.semanticRelationshipProfile;
    }
}
/**
 * @returns Returns the semantic relationship - if null is returned then the relationship simply does not exist at all
 * (it is not even class profile like relationship)
 */
function getSemanticRelationshipBundle(identifier, extractedModels) {
    let semanticRelationship;
    semanticRelationship = extractedModels.relationships.find(rBundle => rBundle.semanticRelationship.id === identifier);
    if (semanticRelationship !== undefined) {
        return {
            semanticRelationship,
            type: "outgoingRelationshipEdges",
        };
    }
    semanticRelationship = extractedModels.generalizations.find(gBundle => gBundle.semanticGeneralization.id === identifier);
    if (semanticRelationship !== undefined) {
        return {
            semanticRelationship,
            type: "outgoingGeneralizationEdges",
        };
    }
    semanticRelationship = extractedModels.relationshipsProfiles.find(rpBundle => rpBundle.semanticRelationshipProfile.id === identifier);
    if (semanticRelationship !== undefined) {
        return {
            semanticRelationship,
            type: "outgoingProfileEdges",
        };
    }
    semanticRelationship = extractedModels.classesProfiles.find(cpBundle => cpBundle.semanticClassProfile.id === identifier);
    if (semanticRelationship !== undefined) {
        return {
            semanticRelationship,
            type: "outgoingClassProfileEdges",
        };
    }
    return null;
}
/**
 * @returns Returns true if either both ends are outsiders or at one is outsider and the other one is in visual model.
 */
const checkIfEdgeShouldBePartOfGraph = (visualModel, entitiesToLayout, start, end) => {
    return isNodeInVisualModel(visualModel, entitiesToLayout, null, start) &&
        isNodeInVisualModel(visualModel, entitiesToLayout, null, end);
};
export function isSubgraph(parentGraph, possibleSubgraph) {
    const nodeInGraph = parentGraph.nodes[possibleSubgraph];
    const isSubgraphTest = nodeInGraph !== undefined && ("nodes" in nodeInGraph);
    return isSubgraphTest;
}
//# sourceMappingURL=graph.js.map